<template>
  <motlet-neo :options="motletOptions">
    <template v-slot:content="{ props }">
      <bar-chart ref="barChart" v-if="isBarChart" :settings="settings" :motlet="props.motlet" :store-name="storeName" />
      <!-- eslint-disable-next-line prettier/prettier -->
      <line-chart
        v-else
        :settings="settings"
        :motlet="props.motlet"
        :store-name="storeName"
        :active-increment="activeIncrement"
      />
    </template>
  </motlet-neo>
</template>

<script>
// BEGIN::Added.by.SHJ.2026.04.14.
import { merge, isEmpty } from 'lodash';
import { format } from 'date-fns';
import { api } from '@/api/common/instances';
import { comma, kilobytes } from '@/utils/formatter';
import bus from '@/utils/bus';
import BarChart from '../../../components/BarChart';
import lineChartable from './lineChartable';
import chart from '@/components/motlet/settings/chart';

const MEMORY_TYPES = chart.C_ABAP_MEMORY.memoryTypes;
// areaName 목록 (usedRate 계산에 사용)
const AREA_NAMES = MEMORY_TYPES.filter(t => t.code !== 'ALL').map(t => t.code);

export default {
  components: { BarChart },
  mixins: [lineChartable],
  props: {
    timestamp: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      barData: [],
      socketData: [],
      currentMemoryType: 'ALL',
      currentChartType: 'bar',
      motletOptions: {
        id: 'dv/abap/prcs/sapMemory',
        title: 'dv.title.sap.memory',
        monitor: {
          monitorCode: 'C_ABAP_MEMORY',
          eventCode: 'ABAP_MEMORY',
          categoryField: 'areaName',
          valueField: 'usedRate',
        },
        settings: { chart: { use: true } },
        memoryType: {
          use: true,
          types: MEMORY_TYPES,
          selected: 'ALL',
        },
        chartType: {
          use: true,
          selected: 'bar',
        },
        xAxisPoints: {
          use: true,
          divider: true,
          ready: false,
          value: 60,
          default: 60,
        },
      },
    };
  },
  computed: {
    isBarChart() {
      return this.currentChartType === 'bar';
    },
    filteredBarData() {
      const memoryType = this.currentMemoryType;
      return this.barData.map(item => ({
        id: item.id,
        data: memoryType === 'ALL' ? item.data : item.data.filter(d => d.areaName?.toUpperCase() === memoryType?.toUpperCase()),
      }));
    },
    // lineChartable의 settings를 override
    settings() {
      if (this.isBarChart) {
        if (this.filteredBarData.length === 0 || !this.filteredBarData.some(d => d.data.length > 0)) {
          return {};
        }
        const percent = this.chartField?.type == 'PERCENT';
        const valueFormatter = this.getValueFormatter();
        return this.buildBarSettings(percent, valueFormatter, this.filteredBarData);
      }
      // line chart
      if (this.data.length === 0) {
        return {};
      }
      const memoryType = this.currentMemoryType;
      const fieldKey = memoryType.toUpperCase().replace(/\s+/g, '_');
      const percent = this.chartField?.type == 'PERCENT';
      const yValue = item => {
        const val = item[fieldKey];
        return val === '' || val === undefined ? '' : val;
      };
      const xAxis = {
        data: this.data[0].data.map(({ ctime }) => ctime),
        axisLabel: {
          interval: Math.ceil(this.data[0].data.length / 6),
        },
      };
      const yAxis = {
        name: `${this.chartField.text} ${percent ? '(%)' : ''}`,
        max: percent ? 100 : undefined,
        maxInterval: percent ? 25 : undefined,
        splitNumber: percent ? 4 : undefined,
      };
      const series = this.data.map(item => {
        const s = {
          type: 'line',
          id: item.id,
          lineStyle: {},
          data: item.data.map((d, index) => [index, yValue(d), d.ctime]),
        };
        const descendant = this.descendants.find(d => d.id == item.id);
        if (!descendant) return s;
        return merge({}, s, {
          lineStyle: {
            color: descendant.color,
            opacity: descendant.active ? 1 : 0.1,
          },
        });
      });
      const valueFormatter = this.getValueFormatter();
      const formatter = {
        tooltip: params => {
          const param = params[0];
          const contents = [];
          contents.push('<i class="v-icon notranslate mdi mdi-clock-time-four-outline mr-1" style="font-size: 12px; line-height: 10px"></i>');
          contents.push(format(parseInt(param.name), 'yyyy-MM-dd HH:mm:ss'));
          contents.push('<br />');
          const data = params.reduce((acc, p) => {
            const descendant = this.descendants.find(d => d.id == p.seriesId);
            if ((descendant || {}).active) {
              const items = [];
              items.push(this.circle(descendant.color));
              items.push(`<span style="color:${descendant.color};font-weight:900;">`);
              items.push(descendant.shortAlias);
              items.push('</span> : ');
              items.push(valueFormatter(p.data[1]));
              items.push('<br />');
              acc.push(items.join(''));
            }
            return acc;
          }, []);
          return contents.concat(data).join('');
        },
      };
      return merge(
        {},
        {
          chart: { xAxis, yAxis, series },
          formatter,
          systemId: this.sstmId,
          storeName: this.storeName,
          datetime: this.datetime,
        },
      );
    },
  },
  watch: {
    // bar chart: timestamp 변경 시 API 호출
    timestamp: {
      immediate: true,
      handler() {
        if (this.isBarChart) this.fetchBarData();
      },
    },
    // bar chart: filteredBarData 변경 시 ECharts 초기화
    filteredBarData: {
      deep: true,
      handler() {
        if (!this.isBarChart) return;
        this.$nextTick(() => {
          const echart = this.$refs.barChart?.echart;
          if (echart) {
            echart.setOption({ dataset: { source: [] }, series: [] });
            echart.setOption(this.$refs.barChart.options);
          }
        });
      },
    },
    // line chart: sstmId 변경 시 재구독 (Queue.vue 패턴)
    sstmId() {
      if (!this.isBarChart && this.activeIncrement && this.motletOptions.xAxisPoints.ready) this.sendMessage();
    },
    // line chart: activeIncrement 변경 시 구독 시작 (Queue.vue 패턴)
    activeIncrement: {
      immediate: true,
      handler(value) {
        if (!this.isBarChart && value && this.motletOptions.xAxisPoints.ready) this.sendMessage();
      },
    },
    // memoryType 변경: sendMessage 재호출 불필요 (모든 areaName 데이터가 저장되어 있으므로 settings가 자동 반응)
    currentChartType(newType) {
      if (newType === 'bar') {
        this.fetchBarData();
      } else {
        if (!this.motletOptions.xAxisPoints.ready) return;
        if (this.activeIncrement) {
          // realtime: line 데이터가 없으면 재구독, 있으면 선택된 타입의 빈 슬롯을 DB에서 backfill
          if (this.data.length === 0) {
            this.sendMessage();
          } else {
            this.backfillFromDb();
          }
        } else {
          // 과거 시점: API로 재조회 (bar 상태에서 datetime이 바뀌었을 수 있음)
          this.fetchData();
        }
      }
    },
  },
  methods: {
    getValueFormatter() {
      return (data, type = this.chartField?.type) => {
        const formatted = { unit: '', data: '' };
        const emptyVal = data === undefined || data === null || data === '';
        data = emptyVal ? '' : data;
        if (type == 'PERCENT') {
          formatted.unit = emptyVal ? '' : ' %';
          formatted.data = data;
        } else if (type == 'DIGIT') {
          formatted.data = comma(data);
        } else if (type == 'KILO_BYTE') {
          formatted.data = kilobytes(data);
        } else {
          formatted.data = data;
        }
        return `${formatted.data}${formatted.unit}`;
      };
    },
    // lineChartable의 fetchData를 override
    // 과거 시점 클릭 시 API로 데이터 조회 (activeIncrement가 false일 때)
    async fetchData() {
      if (this.isBarChart) return;
      if (this.activeIncrement) return;
      if (!this.motletOptions.xAxisPoints.ready) return;
      if (this.currentMemoryType === 'ALL') return;
      const bounds = this.getBounds();
      if (!bounds) return;
      const params = {
        ...bounds,
        sstmId: this.sstmId,
        motletCode: 'C_ABAP_MEMORY_AREA_NAME',
        areaName: this.currentMemoryType,
        ...this.dataParam,
      };
      const { data: results } = await api.get('/motlet-data/chart', { params });
      // API 응답을 WebSocket과 동일한 데이터 구조로 변환
      // 각 포인트에 모든 areaName의 usedRate를 저장
      this.data = results.map(result => {
        const {
          key: { instId: id },
          data,
        } = result;
        // ctime 기준으로 그룹핑
        const grouped = {};
        data.forEach(item => {
          const ctime = parseInt(item.ctime) || parseInt(item.stime);
          if (!grouped[ctime]) {
            grouped[ctime] = { ctime, stime: parseInt(item.stime) || ctime };
            AREA_NAMES.forEach(name => {
              grouped[ctime][name.toUpperCase().replace(/\s+/g, '_')] = '';
            });
          }
          const areaName = item.areaName?.toUpperCase();
          const key = areaName?.replace(/\s+/g, '_');
          if (key) {
            grouped[ctime][key] = parseInt(item.usedRate);
          }
        });
        const sortedData = Object.values(grouped).sort((a, b) => a.ctime - b.ctime);
        return { id, data: sortedData };
      });
    },
    // ========== Bar Chart (API) ==========
    async fetchBarData() {
      const endYmdhms = this.timestamp || Date.now();
      const params = {
        endYmdhms,
        sstmId: this.sstmId,
        motletCode: this.monitor.monitorCode,
      };
      const { data: results } = await api.get('/motlet-data/chart', { params });
      this.barData = results.map(result => {
        const {
          key: { instId: id },
          data,
        } = result;
        return { id, data };
      });
    },
    buildBarSettings(percent, valueFormatter, chartData) {
      const xAxis = {};
      const yAxis = {
        name: `${this.chartField.text} ${percent ? '(%)' : ''}`,
        max: percent ? 100 : undefined,
        maxInterval: percent ? 25 : undefined,
        splitNumber: percent ? 4 : undefined,
      };
      const dataset = (() => {
        const categories = [...new Set(chartData.reduce((acc, item) => acc.concat(item.data.map(s => s.areaName)), []))].sort();
        const header = ['name'].concat(this.descendants.map(d => d.id));
        const rows = categories.map(category => {
          return [category].concat(
            this.descendants
              .filter(d => d.active)
              .map(d => {
                const index = this.descendants.findIndex(item => item.id == d.id);
                const target = chartData[index] ? chartData[index].data.find(item => item.areaName == category) : null;
                return (target || {})[this.chartField.code];
              }),
          );
        });
        return { source: [header].concat(rows) };
      })();
      const series = this.descendants
        .filter(d => d.active)
        .map(d => ({
          name: d.id,
          type: 'bar',
          itemStyle: { color: d.color },
        }));
      const formatter = {
        tooltip: params => {
          const param = params[0];
          const contents = [];
          contents.push('<i class="v-icon notranslate mdi mdi-harddisk mr-1" style="font-size: 12px; line-height: 10px"></i>');
          contents.push(param.name);
          contents.push('<br />');
          params.forEach((p, index) => {
            if (p.data[index + 1] === undefined) return;
            const color = this.descendants.find(d => d.id == p.seriesName).color;
            contents.push(this.circle(color));
            contents.push(`<span style="color:${color};font-weight:900;">`);
            contents.push(this.descendants.find(d => d.id == p.seriesName).shortAlias);
            contents.push('</span> : ');
            contents.push(valueFormatter(p.data[index + 1]));
            contents.push('<br />');
          });
          return contents.join('');
        },
      };
      return { chart: { xAxis, yAxis, dataset, series }, formatter };
    },
    // ========== Line Chart (WebSocket) ==========
    // 모든 areaName의 usedRate를 각 포인트에 저장
    // { ctime, stime, EXTENDED_MEMORY: 4, HEAP_MEMORY: 0, IDs: 98, ... }
    dataHandler(response, ctime, empty) {
      if (isEmpty(response)) response = [{ stime: ctime }];
      const ctimeVal = parseInt(ctime) - (parseInt(ctime) % this.monitor.cycle);
      const data = {
        ctime: ctimeVal,
        stime: parseInt(response[0].stime),
      };
      // 모든 areaName의 값을 저장
      AREA_NAMES.forEach(name => {
        data[name.replace(/\s+/g, '_')] = '';
      });
      if (!empty) {
        response.forEach(r => {
          const areaName = r.areaName?.toUpperCase();
          const key = areaName?.replace(/\s+/g, '_');
          if (key && AREA_NAMES.includes(r.areaName)) {
            if (areaName === 'IDS' || areaName === 'STATEMENT') {
              data[key] = parseInt(r.hitratio);
            } else {
              data[key] = parseInt(r.currUsedP);
            }
          }
        });
      }
      return data;
    },
    wsBufferedDataHandler(key, responses) {
      const [response] = responses;
      const data = response.map(item => {
        return this.dataHandler(item.data, item.ctime, item.empty);
      });
      const existing = this.socketData.findIndex(s => s.id === key.instId);
      if (existing >= 0) {
        this.socketData[existing] = { id: key.instId, data };
      } else {
        this.socketData.push({ id: key.instId, data });
      }
      if (this.socketData.length == this.descendants.length) {
        this.data = merge([], this.socketData);
        this.socketData = [];
        this.prepend(AREA_NAMES.map(n => n.replace(/\s+/g, '_')));
        // WS 버퍼가 차트 X축 개수(monitor.points)만큼 채우지 못한 경우 앞쪽 빈 구간을 DB에서 보충한다.
        // line chart 일 때만, 그리고 선택된 memoryType에 한해서만 보충한다 (C_ABAP_MEMORY_AREA_NAME은 단일 areaName 조회).
        this.backfillFromDb();
      }
    },
    /**
     * WS 버퍼가 monitor.points 만큼 채워지지 않았을 때 DB에서 부족한 앞 구간을 조회해
     * 선택된 memoryType의 비어있는 슬롯에만 값을 주입한다. 실제 WS 데이터는 덮어쓰지 않는다.
     * line chart 전용 (bar chart는 timestamp 기준 단일 스냅샷이므로 불필요).
     */
    async backfillFromDb() {
      if (this.isBarChart) return;
      if (this.currentMemoryType === 'ALL') return;
      if (!this.data || this.data.length === 0) return;
      const firstInst = this.data[0].data;
      if (!firstInst || firstInst.length === 0) return;
      const fieldKey = this.currentMemoryType.toUpperCase().replace(/\s+/g, '_');
      const realPoints = firstInst.filter(d => d[fieldKey] !== undefined && d[fieldKey] !== '');
      if (realPoints.length === firstInst.length) return;
      const beginYmdhms = firstInst[0].ctime;
      const endYmdhms = realPoints.length > 0 ? realPoints[0].ctime - this.monitor.cycle : firstInst[firstInst.length - 1].ctime;
      if (endYmdhms < beginYmdhms) return;
      const params = {
        beginYmdhms,
        endYmdhms,
        sstmId: this.sstmId,
        motletCode: 'C_ABAP_MEMORY_AREA_NAME',
        areaName: this.currentMemoryType,
        ...this.dataParam,
      };
      try {
        const { data: results } = await api.get('/motlet-data/chart', { params });
        if (!results || results.length === 0) return;
        results.forEach(result => {
          const inst = this.data.find(d => d.id === result.key.instId);
          if (!inst) return;
          result.data.forEach(db => {
            const dbCtime = parseInt(db.ctime);
            const slot = inst.data.find(p => p.ctime === dbCtime);
            if (slot && (slot[fieldKey] === undefined || slot[fieldKey] === '')) {
              slot[fieldKey] = parseInt(db.usedRate);
              if (db.stime) slot.stime = parseInt(db.stime);
            }
          });
        });
      } catch (e) {
        // DB 조회 실패 시 차트는 WS 실시간 수신으로 점진적으로 채워지므로 무시한다.
        console.warn('[SapMemory] backfillFromDb failed', e);
      }
    },
    wsDataHandler(key, response, ctime, empty) {
      if (this.data.length === 0) return;
      if (ctime === null || ctime === undefined) return;
      const data = this.dataHandler(response, ctime, empty);
      // 같은 인스턴스의 같은 ctime 데이터는 기존 데이터에 merge
      const existingSocket = this.socketData.find(s => s.id === key.instId);
      if (existingSocket) {
        // 같은 ctime이면 값을 merge
        if (existingSocket.data.ctime === data.ctime) {
          AREA_NAMES.forEach(name => {
            const k = name.replace(/\s+/g, '_');
            if (data[k] !== '' && data[k] !== undefined) {
              existingSocket.data[k] = data[k];
            }
          });
          return;
        }
      } else {
        this.socketData.push({ id: key.instId, data });
      }
      if (this.socketData.length == this.descendants.length) {
        this.socketData.forEach(s => {
          const target = this.data.find(d => d.id == s.id);
          if (target) target.data.push(s.data);
        });
        this.socketData = [];
        this.prepop();
      }
    },
    sendMessage() {
      // this.data는 초기화하지 않는다 (과거 데이터 유지 → datetime 변화로 인한 changeSeries에서 markLine이 즉시 우측으로 이동)
      // WS buffer 도착 시 wsBufferedDataHandler가 this.data를 교체
      this.socketData = [];
      this.$websocket.realtime.send({
        requests: this.descendants.map(descendant => ({
          key: { name: 'rawMemory', sstmId: descendant.systemId, instId: descendant.id },
          fields: ['stime', 'areaName', 'currUsedP', 'hitratio'],
          bufferSize: this.monitor.points,
          handler: {
            buffer: this.wsBufferedDataHandler,
            every: this.wsDataHandler,
          },
        })),
      });
    },
    // ========== Settings ==========
    xPointsChanged(xPoints) {
      this.motletOptions.xAxisPoints.ready = true;
      this.motletOptions.xAxisPoints.value = xPoints;
      this.motletOptions.monitor.points = xPoints;
      this.setXAxisPoints(xPoints);
      this.setMonitor(this.motletOptions.monitor);
      if (!this.isBarChart && this.activeIncrement) this.sendMessage();
    },
    memoryTypeChanged(type) {
      this.currentMemoryType = type;
      this.motletOptions.memoryType.selected = type;
      if (type === 'ALL' && this.currentChartType === 'line') {
        this.currentChartType = 'bar';
        this.motletOptions.chartType.selected = 'bar';
      }
      this.updateTitle();
      if (!this.isBarChart && type !== 'ALL') {
        if (!this.activeIncrement) {
          // line chart + 과거 시점: C_ABAP_MEMORY_AREA_NAME는 선택된 areaName만 조회하므로 타입 변경 시 재조회
          this.fetchData();
        } else {
          // line chart + 실시간: 새로 선택된 타입의 빈 슬롯을 DB에서 backfill
          this.backfillFromDb();
        }
      }
    },
    chartTypeChanged(type) {
      this.currentChartType = type;
      this.motletOptions.chartType.selected = type;
    },
    updateTitle() {
      this.motletOptions.subTitle = this.currentMemoryType === 'ALL' ? '' : ` (${this.currentMemoryType})`;
    },
  },
  created() {
    bus.$on(`update:memoryType-${this.motletOptions.id}`, type => this.memoryTypeChanged(type));
    bus.$on(`update:chartType-${this.motletOptions.id}`, type => this.chartTypeChanged(type));
    bus.$on(`update:xAxisPoints-${this.motletOptions.id}`, xAxisPoints => {
      bus.$emit('update:activeIncrement', false);
      this.xPointsChanged(xAxisPoints);
    });
  },
  destroyed() {
    bus.$off(`update:memoryType-${this.motletOptions.id}`);
    bus.$off(`update:chartType-${this.motletOptions.id}`);
    bus.$off(`update:xAxisPoints-${this.motletOptions.id}`);
  },
};
// FINISH::Added.by.SHJ.2026.04.14.
</script>
